import { Divider, Menu } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import AddProjectIcon from '../../../assets/img/AddProjectIcon.svg';
import ProjectIcon from '../../../assets/img/ProjectIcon.svg';
import * as routing from '../../../constants/Routing';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveFlatProjects } from '../../../hooks/project/useRetrieveFlatProjects';
import { useAsUser } from '../../../hooks/session/useAsUser';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';

const CustomMenuItem = styled.div`
  width: 100%;
  min-width: 256px;
  height: 48px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ProjectContainer = styled.div.attrs((props: { asUser: boolean }) => ({
  asUser: props.asUser,
}))`
  width: 62px;
  height: 61px;
  min-height: 61px;
  background: ${(props) => (!props.asUser ? RoutalPalette.neutral00 : `tomato`)};
  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;
  border-bottom: 1px solid ${RoutalPalette.neutral10};
`;

export const SideBarProject = () => {
  const [projectAnchorEl, setProjectAnchorEl] = useState<(EventTarget & HTMLDivElement) | null>(null);
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: projects } = useRetrieveFlatProjects({ user });
  const { asUser } = useAsUser();
  const navigate = useNavigate();

  const projectsCallback = (type: any) => {
    switch (type) {
      case `manage-projects`:
        navigate(routing.SETTINGS.ORGANIZATION.PROJECTS);
        break;
      case `add-project`:
        navigate(`${routing.SETTINGS.ORGANIZATION.PROJECTS}?create=true`);
        break;
      default:
        break;
    }
  };

  const handleProjectItemAction = (action: any) => {
    setProjectAnchorEl(null);
    action.onClick && action.onClick();
  };

  const projectActions = [
    ...(user && projects
      ? [
          ...(projects.length !== 0
            ? [
                ...projects
                  .sort((pA, pB) => {
                    // Sort projects alphabetically by label that could be optional
                    if (pA.label && pB.label) {
                      return pA.label.localeCompare(pB.label);
                    } else if (pA.label) {
                      return -1;
                    } else if (pB.label) {
                      return 1;
                    } else {
                      return 0;
                    }
                  })
                  .map((project) => ({
                    id: `${project.id}`,
                    label: (
                      <CustomMenuItem
                        style={{
                          justifyContent: `flex-start`,
                          padding: `0 8px`,
                          cursor: activeProject?.id === project.id ? `default` : `pointer`,
                        }}
                        onClick={async () => {
                          if (activeProject?.id === project.id) return;
                          window.open(`${routing.PLANNER.ROOT.replace(`:projectId`, project.id)}`, `_self`);
                        }}
                      >
                        <RoutalAvatar
                          url={project.avatar_url}
                          getUploadAsBase64={getUploadAsBase64}
                          label={project.label}
                          labelStyles={{ fontSize: `12px`, lineHeight: 2 }}
                          containerStyles={{ width: `32px`, height: `32px` }}
                        />
                        <HSpacer small />
                        <Text style={{ cursor: activeProject?.id === project.id ? `default` : `pointer` }}>
                          {project.label}
                        </Text>
                      </CustomMenuItem>
                    ),
                  })),
              ]
            : [
                {
                  label: (
                    <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `0 12px` }}>
                      <Text style={{ cursor: `pointer` }} intlId="projects.not_assigned_projects.leftbar" />
                    </CustomMenuItem>
                  ),
                },
              ]),
          {
            isDivider: true,
          },
          {
            id: `manage-projects`,
            name: `manage-projects`,
            onClick: () => {
              projectsCallback && projectsCallback(`manage-projects`);
            },
            label: (
              <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `0 12px` }}>
                <img src={ProjectIcon} alt="manage_projects" />
                <Text style={{ cursor: `pointer` }} intlId="projects.manage_all" />
              </CustomMenuItem>
            ),
          },
          {
            id: `add-project`,
            // name: `add-project`,
            onClick: () => {
              projectsCallback && projectsCallback(`add-project`);
            },
            label: (
              <CustomMenuItem style={{ justifyContent: `flex-start`, padding: `0 12px` }}>
                <img src={AddProjectIcon} alt="add_new_project" />
                <Text style={{ cursor: `pointer` }} intlId="projects.add_new" />
              </CustomMenuItem>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <RoutalTooltip id="current_project" title={activeProject?.label || `Default project`} placement="right">
        <ProjectContainer onClick={(e) => setProjectAnchorEl(e.currentTarget)} asUser={!!asUser && asUser !== ``}>
          {activeProject && (
            <RoutalAvatar
              url={activeProject.avatar_url}
              getUploadAsBase64={getUploadAsBase64}
              label={activeProject.label}
              containerStyles={{ width: `42px`, height: `42px` }}
            />
          )}
        </ProjectContainer>
      </RoutalTooltip>
      {user && projectActions !== undefined && projectActions.length !== 0 && (
        <Menu
          id="project-actions"
          anchorEl={projectAnchorEl}
          open={Boolean(projectAnchorEl)}
          anchorOrigin={{ horizontal: `right`, vertical: `bottom` }}
          onClose={(_e) => setProjectAnchorEl(null)}
        >
          {projectActions.map((action, i) =>
            (action as any).isDivider ? (
              <Divider key={`divider${i}`} />
            ) : (
              <div key={action.id} style={{ width: `100%` }} onClick={() => handleProjectItemAction(action)}>
                {action.label}
              </div>
            )
          )}
        </Menu>
      )}
    </>
  );
};
